// src/QRmessaging/QRScanner.js

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import './QRScanner.css';

const QRScanner = ({ language }) => {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processQRCode(file);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile();
        processQRCode(blob);
        break;
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processQRCode(files[0]);
    }
  };

  const processQRCode = (file) => {
    if (!file.type.match('image.*')) {
      setError(language === 'KR' 
        ? '이미지 파일만 사용할 수 있습니다.' 
        : 'Only image files are supported.');
      return;
    }

    setIsProcessing(true);
    setError(null);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        // 캔버스 크기를 이미지 크기에 맞게 설정
        canvas.width = img.width;
        canvas.height = img.height;
        
        // 이미지를 캔버스에 그리기
        ctx.drawImage(img, 0, 0);
        
        // 이미지 데이터 가져오기
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        
        // QR 코드 디코딩
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        setIsProcessing(false);
        
        if (code) {
          setResult(code.data);
        } else {
          setError(language === 'KR' 
            ? '이미지에서 QR 코드를 찾을 수 없습니다.' 
            : 'No QR code found in the image.');
        }
      };
      img.src = e.target.result;
    };
    reader.onerror = () => {
      setIsProcessing(false);
      setError(language === 'KR' 
        ? '파일을 읽는 도중 오류가 발생했습니다.' 
        : 'Error reading the file.');
    };
    reader.readAsDataURL(file);
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  useEffect(() => {
    // 페이지에 붙여넣기 이벤트 리스너 추가
    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, []);

  const handleGoToLink = () => {
    // QR 코드가 URL인지 확인
    if (result.startsWith('http://') || result.startsWith('https://')) {
      window.open(result, '_blank');
    } 
    // doldari QR 메시지인지 확인
    else if (result.includes('/qr/view?id=')) {
      navigate(result.split(window.location.origin)[1] || result);
    }
  };

  return (
    <div className="qr-scanner-client-container">
      <header className="qr-scanner-header">
      <h1>{language === 'KR' ? 'QR 코드 스캐너' : 'QR Code Scanner'}</h1>
      </header>
      <main className="qr-scanner-main">
      <div className="qr-scanner">
        <h2>{language === 'KR' ? 'QR 코드 스캔하기' : 'Scan QR Code'}</h2>
        
        <div 
          className={`qr-dropzone ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={triggerFileInput}
        >
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileChange} 
            accept="image/*" 
            style={{ display: 'none' }}
          />
          <div className="qr-dropzone-content">
            <i className="fas fa-qrcode qr-icon"></i>
            <p>
              {isMobile
                ? (language === 'KR' 
                  ? '사진 폴더에서 QR 코드 이미지를 선택하거나 이곳에 붙여넣기 하세요' 
                  : 'Select a QR code from your photos or paste here')
                : (language === 'KR' 
                  ? '이미지를 끌어다 놓거나, 클릭하여 파일을 선택하거나, 붙여넣기(Ctrl+V)로 QR 코드를 스캔하세요' 
                  : 'Drop an image here, click to select a file, or paste (Ctrl+V) to scan a QR code')}
            </p>
          </div>
        </div>

        {isProcessing && (
          <div className="qr-loading">
            <div className="qr-spinner"></div>
            <p>{language === 'KR' ? 'QR 코드 처리 중...' : 'Processing QR code...'}</p>
          </div>
        )}

        {error && (
          <div className="qr-error">
            <p>{error}</p>
            <button 
              className="qr-btn" 
              onClick={() => setError(null)}
            >
              {language === 'KR' ? '다시 시도' : 'Try Again'}
            </button>
          </div>
        )}

        {result && (
          <div className="qr-result">
            <h3>{language === 'KR' ? 'QR 코드 내용' : 'QR Code Content'}</h3>
            <div className="qr-result-content">
              <p>{result}</p>
            </div>
            <div className="qr-result-actions">
              {(result.startsWith('http://') || result.startsWith('https://') || result.includes('/qr/view?id=')) && (
                <button 
                  className="qr-btn" 
                  onClick={handleGoToLink}
                >
                  {language === 'KR' ? '링크로 이동' : 'Go to Link'}
                </button>
              )}
              <button 
                className="qr-btn qr-btn-secondary" 
                onClick={() => {
                  setResult(null);
                  setError(null);
                }}
              >
                {language === 'KR' ? '다른 QR 코드 스캔' : 'Scan Another QR Code'}
              </button>
            </div>
          </div>
        )}

        <canvas 
          ref={canvasRef} 
          style={{ display: 'none' }}
        />
      </div>
      </main>
    </div>
  );
};

export default QRScanner;